// ============================================================================
// 建物の影ポリゴン（透視投影）
// ----------------------------------------------------------------------------
// /Users/ntuf/Workspace/ShadowStep の src/lib/shadows.ts は太陽（無限遠の平行光線）
// を前提に「各頂点を影方位へ一定距離だけ平行移動→凸包」という手法を使っている。
// 花火の打ち上げ地点は近距離の点光源なので、平行移動ではなく光源を中心とした
// 相似拡大（透視投影）になる：
//
//   光源の地上高 hL（打ち上げ地点の標高からの高さ）
//   建物の高さ   hB
//   拡大率       k = hL / (hL - hB)   ※ hL > hB のとき、頂点Vの影は
//                                       光源Lから見てVのk倍遠い位置に落ちる
//
// k倍した頂点群と元の頂点群を合わせた凸包を「影ポリゴン」とする
// （ShadowStepの「元フットプリント∪移動後フットプリントの凸包」と同じ考え方）。
// ============================================================================

import { clipRingToCircle, convexHull } from "./geo";
import type { Building, LngLat } from "./types";

/** 光源とほぼ同じ高さ(またはそれ以上)の建物で影が無限に伸びるのを防ぐ上限。 */
const MAX_SHADOW_FACTOR = 5;

export interface BuildingShadow {
  id: number;
  ring: LngLat[];
  /** 建物位置でのDEM標高(m)。号数変更時にメインスレッドで影だけ再計算する際に使う。 */
  groundElevation: number;
}

/**
 * 1棟ぶんの影ポリゴンを計算する。
 * @param groundElevation 建物位置でのDEM標高(m)。光源の地上高を求めるのに使う。
 * @param maxDistanceM 指定時、光源からこの距離(m、肉眼での限界距離を想定)を
 *   超える部分を切り取る。境界付近の建物の影がMAX_SHADOW_FACTOR倍に伸びて
 *   限界距離の外まではみ出して見えるのを防ぐ。
 */
export function buildingShadowRing(
  building: Building,
  lightLng: number,
  lightLat: number,
  lightHeight: number,
  groundElevation: number,
  maxDistanceM?: number
): LngLat[] {
  const lightAboveGround = lightHeight - groundElevation;
  const k =
    lightAboveGround <= building.height
      ? MAX_SHADOW_FACTOR
      : Math.min(lightAboveGround / (lightAboveGround - building.height), MAX_SHADOW_FACTOR);

  const scaled: LngLat[] = building.ring.map(([lng, lat]) => [
    lightLng + (lng - lightLng) * k,
    lightLat + (lat - lightLat) * k,
  ]);
  const hull = convexHull([...building.ring, ...scaled]);
  if (maxDistanceM === undefined) return hull;
  return clipRingToCircle(hull, [lightLng, lightLat], maxDistanceM);
}
